import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import useOrderFormParams from '@/modules/order/hooks/use-order-form-params.hook'
import {
  useOrderFormStore,
  useOrderFormStoreActions,
} from '@/modules/order/stores/order-form.store'

import {
  changeCommaForDot,
  changeDotForComma,
  fixedTwoDecimal,
} from '../helpers/helper'
import { useProductStoreActions } from '../stores/product.store'
import {
  type TSchemaValidation,
  schemaValidation,
} from '../validations/product-details.validation'
import { useCalculationProductDetails } from './use-calculation-product-details-form.hook'
import { useControllerProductHook } from './use-controller-product.hook'
import { useGetParamsLayoutProductDetailsHook } from './use-get-params-layout-product-details.hook'
import { useGetProductFromOrderHook } from './use-get-product-from-order.hook'
import { useGetSearchProductHook } from './use-get-search-product.hook'
import { useProductStockLimit } from './use-product-stock-limit.hook'

export const useProductDetailsFormHook = () => {
  const [unitaryBase, setUnitaryBase] = useState(0)
  const [roundedQuantityMessage, setRoundedQuantityMessage] = useState('')
  const [isCalculatingVolumes, setIsCalculatingVolumes] = useState(false)
  const [quantityError, setQuantityError] = useState(false)

  const { product: searchProduct } = useGetSearchProductHook()
  const { product: productFromOrder } = useGetProductFromOrderHook()
  const product = productFromOrder || searchProduct
  const { limitaQuantidadePorEstoque, maximumQuantity, limitQuantityToStock, stockWarning } = useProductStockLimit(product)
  const packageQuantity = product?.produto?.qtdeEmbalagem || 1

  const { isCalculationOrder, jsonOrderForm } = useOrderFormStore()
  const { setIsCalculationOrder } = useOrderFormStoreActions()
  const { setIsOpenModalDetailsProduct } = useProductStoreActions

  const { getParamsLayout } = useGetParamsLayoutProductDetailsHook()
  const fixedValueParams = getParamsLayout('fixedValue')
  const quantityParams = getParamsLayout('quantityDetails')
  const discountParams = getParamsLayout('discountDetails')
  const unitaryValueParams = getParamsLayout('unitaryValueDetails')
  const showCommissionParams = getParamsLayout('showCommissionDetails')

  const { addOrUpdateProductOnOrder } = useControllerProductHook(product)
  const { calcDiscountWithUnitaryValue, calcUnitaryValueWithDiscount } =
    useCalculationProductDetails(product)

  const accordionPercentageCommission = product?.porcComissao
  const accordionCommissionValue = changeDotForComma(
    fixedTwoDecimal((product?.bcComissao * product?.porcComissao) / 100),
  )

  const { isViewOrder } = useOrderFormParams()

  const form = useForm<TSchemaValidation>({
    resolver: schemaValidation,
    defaultValues: {
      fixedValue: product?.flagPrecoFixo || false,
      quantity: product?.quantidade || 0,
      discount: changeDotForComma(fixedTwoDecimal(product?.porcDescontoTabela || 0)),
      unitaryValue: changeDotForComma(fixedTwoDecimal(product?.valorUnitario || 0, 2)),
      packageQuantity: product?.produto?.qtdeEmbalagem || 1,
      volumes: product?.quantidade
        ? product.quantidade / (product?.produto?.qtdeEmbalagem || 1)
        : 0,
    },
  })

  const { watch, reset, setValue } = form

  const validateQuantityImmediate = (quantity: number) => {
    if (jsonOrderForm.operacao.flagValidaQtdEmbalagem && quantity > 0 && quantity % packageQuantity !== 0) {
      setQuantityError(true)
      return false
    }
    setQuantityError(false)
    return true
  }

  const validateMaxDiscount = (discountValue: number) => {
    if (!product?.porcMaxDesconto || product.porcMaxDesconto === 0) {
      return discountValue <= 100
    }
    return discountValue <= product.porcMaxDesconto
  }

  const handleChangeDiscount = (newDiscountValue?: string) => {
    const discountValue = newDiscountValue
      ? Number(changeCommaForDot(newDiscountValue))
      : Number(changeCommaForDot(form.getValues().discount))

    if (discountValue < 0) return

    if (!validateMaxDiscount(discountValue)) {
      const maxDiscount = product?.porcMaxDesconto && product.porcMaxDesconto > 0 ? product.porcMaxDesconto : 100
      toast.info(`Desconto máximo permitido: ${maxDiscount}%`)
      return
    }

    const { newUnitaryValue, newUnitaryValueBase } =
      calcUnitaryValueWithDiscount(discountValue)

    const valueFormatted = changeDotForComma(fixedTwoDecimal(newUnitaryValue))

    setUnitaryBase(newUnitaryValueBase ?? 0)
    setValue('unitaryValue', valueFormatted, { shouldValidate: true })
    setValue('fixedValue', discountValue !== 0, { shouldValidate: true })
  }

  const handleChangeUnitaryValue = (newUnitaryValue?: string) => {
    const unitaryValue = newUnitaryValue
      ? Number(changeCommaForDot(newUnitaryValue))
      : Number(changeCommaForDot(form.getValues().unitaryValue))

    if (unitaryValue <= 0) return

    const { newUnitaryValue: newDiscount, newUnitaryValueBase } =
      calcDiscountWithUnitaryValue(unitaryValue)

    const valueFormatted = changeDotForComma(
      fixedTwoDecimal(Math.max(newDiscount, 0)),
    )

    setUnitaryBase(newUnitaryValueBase ?? 0)
    setValue('discount', valueFormatted, { shouldValidate: true })
    setValue(
      'fixedValue',
      unitaryValue.toFixed(2) !== (product?.valorUnitario || 0).toFixed(2),
      { shouldValidate: true }
    )
  }

  const handleQuantityChange = (quantity: number) => {
    const safeQuantity = limitQuantityToStock(Math.max(0, quantity || 0))
    setValue('quantity', safeQuantity, { shouldValidate: true })
    validateQuantityImmediate(safeQuantity)
  }

  const checkAndActiveOptionToCalculationOrder = () => {
    if (!isCalculationOrder) setIsCalculationOrder(true)
  }

  const calculatenumberOfVolumes = (quantity: number | undefined) => {
    if (quantity === 0 || quantity === undefined) {
      setValue('volumes', quantity === 0 ? 0 : undefined, { shouldValidate: true })
      setRoundedQuantityMessage('')
      return
    }

    const numericQuantity = Number(quantity)
    if (Number.isNaN(numericQuantity) || numericQuantity === 0) {
      setValue('volumes', undefined, { shouldValidate: true })
      setRoundedQuantityMessage('')
      return
    }

    const newVolume = numericQuantity / packageQuantity

    // O teto já considera o maior múltiplo da embalagem que cabe no estoque.
    if (limitaQuantidadePorEstoque && numericQuantity >= maximumQuantity) {
      const limitedQuantity = limitQuantityToStock(numericQuantity)
      setValue('quantity', limitedQuantity, { shouldValidate: true })
      setValue('volumes', limitedQuantity / packageQuantity, { shouldValidate: true })
      setRoundedQuantityMessage('')
      return
    }

    if (!Number.isInteger(newVolume) && newVolume > 0) {
      const roundedVolume = Math.floor(newVolume)
      const calculatedQuantity = roundedVolume * packageQuantity

      if (calculatedQuantity === 0) {
        const roundedToUp = Math.ceil(newVolume)
        const calculatedQuantity = limitQuantityToStock(roundedToUp * packageQuantity)
        setValue('volumes', calculatedQuantity / packageQuantity, { shouldValidate: true })
        setValue('quantity', calculatedQuantity, { shouldValidate: true })

        return setRoundedQuantityMessage(
          `Qtd. ${quantity} inválida! Valor ajustado.`,
        )
      }

      setValue('quantity', calculatedQuantity, { shouldValidate: true })
      setValue('volumes', roundedVolume, { shouldValidate: true })
      setRoundedQuantityMessage(`Qtd. ${quantity} inválida! Valor ajustado.`)
    } else {
      setValue('volumes', newVolume, { shouldValidate: true })
      setRoundedQuantityMessage('')
    }
  }

  const calculateNumberOfProducts = (volumes: number | undefined): void => {
    if (volumes === undefined || volumes === 0 || Number.isNaN(volumes)) {
      limitQuantityToStock(0)
      setValue('quantity', volumes === 0 ? 0 : undefined, { shouldValidate: true })
      return
    }

    const requestedQuantity = limitaQuantidadePorEstoque && volumes === maximumQuantity / packageQuantity
      ? maximumQuantity
      : volumes * packageQuantity
    const numberOfProducts = limitQuantityToStock(requestedQuantity)

    setValue('quantity', numberOfProducts, { shouldValidate: true })

    if (limitaQuantidadePorEstoque && numberOfProducts >= maximumQuantity) {
      setValue('volumes', numberOfProducts / packageQuantity, { shouldValidate: true })
    } else if (numberOfProducts === 0) {
      setValue('volumes', 0, { shouldValidate: true })
    } else if (jsonOrderForm.operacao.flagValidaQtdEmbalagem) {
      setValue('volumes', Math.floor(numberOfProducts / packageQuantity), { shouldValidate: true })
    }
  }

  const handleCloseDetails = () => {
    setIsOpenModalDetailsProduct(false)

    reset({
      discount: fixedTwoDecimal(0),
      fixedValue: false,
      quantity: undefined,
      unitaryValue: fixedTwoDecimal(0),
    })
  }

  const handleSaveChanges = async (data: TSchemaValidation) => {
    if (isViewOrder) return
    const finalQuantity = limitQuantityToStock(Math.max(0, data.quantity ?? 0))
    setValue('quantity', finalQuantity, { shouldValidate: true })

    if (jsonOrderForm.operacao.flagValidaQtdEmbalagem && finalQuantity > 0 && finalQuantity % packageQuantity !== 0) {
      toast.info('Quantidade deve ser múltipla da quantidade de embalagem')
      return
    }

    const finalDiscount = Number(changeCommaForDot(data.discount))
    if (!validateMaxDiscount(finalDiscount)) {
      const maxDiscount = product?.porcMaxDesconto && product.porcMaxDesconto > 0 ? product.porcMaxDesconto : 100
      toast.info(`Desconto máximo permitido: ${maxDiscount}%`)
      return
    }

    addOrUpdateProductOnOrder({
      flagPrecoFixo: data.fixedValue,
      porcDescontoTabela: finalDiscount,
      quantidade: finalQuantity,
      valorUnitario: Number(changeCommaForDot(data.unitaryValue)),
      valorUnitarioBase: Number(changeCommaForDot(unitaryBase.toString())),
    })

    checkAndActiveOptionToCalculationOrder()
    handleCloseDetails()
  }

  const watchedQuantity = watch('quantity')
  const watchedVolumes = watch('volumes')

  useEffect(() => {
    if (watchedQuantity !== undefined && watchedQuantity >= 0) {
      if (!isViewOrder && limitaQuantidadePorEstoque && watchedQuantity > maximumQuantity) {
        setValue('quantity', limitQuantityToStock(watchedQuantity), { shouldValidate: true })
        return
      }
      validateQuantityImmediate(watchedQuantity)
    }
  }, [watchedQuantity, packageQuantity, jsonOrderForm.operacao.flagValidaQtdEmbalagem, limitaQuantidadePorEstoque, maximumQuantity, isViewOrder])

  useEffect(() => {
    if (!jsonOrderForm.operacao.flagValidaQtdEmbalagem) {
      return
    }

    if (watchedQuantity === 0) {
      setRoundedQuantityMessage('')
      calculatenumberOfVolumes(0)
      return
    }

    if (watchedQuantity === undefined) {
      setValue('volumes', undefined, { shouldValidate: true })
      setRoundedQuantityMessage('')
      return
    }

    if (limitaQuantidadePorEstoque && watchedQuantity >= maximumQuantity) {
      calculatenumberOfVolumes(watchedQuantity)
      setIsCalculatingVolumes(false)
      return
    }

    if (watchedQuantity % packageQuantity === 0) {
      calculatenumberOfVolumes(watchedQuantity)
      setIsCalculatingVolumes(false)
      return
    }

    setIsCalculatingVolumes(true)
    const timeout = setTimeout(() => {
      calculatenumberOfVolumes(watchedQuantity)
      setIsCalculatingVolumes(false)
    }, 1000)

    return () => clearTimeout(timeout)
  }, [watchedQuantity, packageQuantity, jsonOrderForm.operacao.flagValidaQtdEmbalagem, limitaQuantidadePorEstoque, maximumQuantity])

  useEffect(() => {
    calculateNumberOfProducts(watchedVolumes)
  }, [watchedVolumes, packageQuantity])

  return {
    isViewOrder,
    packageQuantity,
    product,
    form,
    handleSaveChanges,
    handleChangeDiscount,
    accordionCommissionValue,
    handleChangeUnitaryValue,
    accordionPercentageCommission,
    calculateNumberOfProducts,
    calculatenumberOfVolumes,
    roundedQuantityMessage,
    isCalculatingVolumes,
    quantityError,
    stockWarning,
    handleQuantityChange,
    validateQuantityImmediate,
    params: {
      discountParams,
      quantityParams,
      fixedValueParams,
      unitaryValueParams,
      showCommissionParams,
    },
  }
}
